'use client';

import { useState, useEffect, useTransition } from 'react';
import { User, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { Button } from '@kit/ui/button';
import { Card, CardContent } from '@kit/ui/card';
import { Label } from '@kit/ui/label';
import { Skeleton } from '@kit/ui/skeleton';

import type { ChildAccount, SelectedAccount } from '../_lib/types';

interface AccountSelectorProps {
  userId: string;
  userName?: string;
  tenantId?: string;
  selectedAccountId: string | null;
  onChange: (account: SelectedAccount) => void;
  className?: string;
  disabled?: boolean;
}

/**
 * AccountSelector - Button-based single-select component for choosing who a booking is for
 *
 * Displays a grid of buttons for the user and their children.
 * Only one account can be selected at a time.
 * If the user has no children, this component renders nothing.
 *
 * @param userId - The authenticated user's ID (parent account ID)
 * @param userName - The authenticated user's name (optional, defaults to "Myself")
 * @param tenantId - Optional tenant ID to filter children by tenant
 * @param selectedAccountId - Currently selected account ID
 * @param onChange - Callback when selection changes
 * @param className - Optional CSS classes
 */
export function AccountSelector({
  userId,
  userName,
  tenantId,
  selectedAccountId,
  onChange,
  className,
  disabled = false,
}: AccountSelectorProps) {
  const client = useSupabase();
  const { t } = useTranslation();
  const [children, setChildren] = useState<ChildAccount[]>([]);
  const [isLoading, startTransition] = useTransition();
  const [hasLoaded, setHasLoaded] = useState(false);

  // Load children on mount
  useEffect(() => {
    if (!userId) return;

    startTransition(async () => {
      try {
        const { data, error } = await (client.rpc as any)('get_user_children', {
          p_user_id: userId,
        });

        if (error) {
          console.error('Error loading children:', error);
          setChildren([]);
        } else {
          const typedData = (data || []) as ChildAccount[];
          const filteredChildren = tenantId
            ? typedData.filter((c) => c.tenant_id === tenantId)
            : typedData;
          setChildren(filteredChildren);
        }
      } catch (error) {
        console.error('Error loading children:', error);
        setChildren([]);
      } finally {
        setHasLoaded(true);
      }
    });
  }, [userId, tenantId, client]);

  // Auto-select self on first load if nothing is selected
  useEffect(() => {
    if (hasLoaded && selectedAccountId === null) {
      onChange({
        id: userId,
        name: userName || t('bookings:bookForSelf'),
        isChild: false,
      });
    }
  }, [hasLoaded, selectedAccountId, userId, userName, onChange, t]);

  // If no children, don't render anything (default to self)
  if (hasLoaded && children.length === 0) {
    return null;
  }

  // Loading state
  if (isLoading || !hasLoaded) {
    return (
      <Card className={className}>
        <CardContent className="p-2 md:p-4">
          <Label className="text-sm font-medium mb-3 block">{t('bookings:bookFor')}</Label>
          <div className="flex flex-wrap gap-2">
            <Skeleton className="h-10 w-24" />
            <Skeleton className="h-10 w-24" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const handleSelect = (account: SelectedAccount) => {
    onChange(account);
  };

  return (
    <Card className={className}>
      <CardContent className="p-2 md:p-4">
        <Label className="text-sm font-medium mb-3 block">{t('bookings:bookFor')}</Label>

        <div className="flex flex-wrap gap-2">
          {/* Self button */}
          <Button
            type="button"
            disabled={disabled}
            variant={selectedAccountId === userId ? 'default' : 'outline'}
            className="flex items-center gap-2"
            onClick={() =>
              handleSelect({
                id: userId,
                name: userName || t('bookings:bookForSelf'),
                isChild: false,
              })
            }
          >
            <User className="h-4 w-4" />
            <span>{userName || t('bookings:bookForSelf')}</span>
          </Button>

          {/* Children buttons */}
          {children.map((child) => (
            <Button
              key={child.child_account_id}
              type="button"
              disabled={disabled}
              variant={
                selectedAccountId === child.child_account_id
                  ? 'default'
                  : 'outline'
              }
              className="flex items-center gap-2"
              onClick={() =>
                handleSelect({
                  id: child.child_account_id,
                  name: child.child_name,
                  isChild: true,
                })
              }
            >
              <Users className="h-4 w-4" />
              <span>{child.child_name}</span>
            </Button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
